import * as React from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import {
  AttendancePeriod,
  BulkCreateAttendanceShiftShiftsInput,
  TimesheetPageShiftFragment,
} from 'generated/resources/graphql'
import moment, { Moment } from 'moment'

import asyncAction from 'modules/core/lib/asyncAction'
import { dateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'
import { apiClient as ApiClient } from 'modules/core/lib/mobx-rest'
import app from 'modules/core/store/models/app'

import type Store from 'modules/attendance/components/ClockIn/Store'
import { useShiftActions } from 'modules/attendance/hooks/shifts/useShiftActions'
import { useAttendanceDateNavigator } from 'modules/attendance/lib/attendanceDateNavigatorContext'
import { useTimesheetData } from 'modules/attendance/lib/timesheetDataContext'
import { TimesheetFeatures } from 'modules/attendance/lib/timesheetFeatures'
import AttendanceShiftUtils, { HalfDayTypes } from 'modules/attendance/lib/utils/AttendanceShift'

import { getDateRange } from '../../EmployeeTimeTrackingBreakdown/lib/getDateRange'
import DailyCounter from './DailyCounter'
import DailyTable from './DailyTable'

function halfDayShiftsFor({
  date,
  employeeId,
  shifts,
}: {
  date: string
  employeeId: number
  shifts: TimesheetPageShiftFragment[]
}): TimesheetPageShiftFragment[] {
  const dayShifts = shifts.filter(
    (shift) => shift.date === date && AttendanceShiftUtils.isHalfDay(shift)
  )

  if (dayShifts.length === 2) return dayShifts

  const firstShift = dayShifts[0]

  if (!firstShift) {
    return [
      AttendanceShiftUtils.newShift<TimesheetPageShiftFragment>({
        date,
        employeeId,
        halfDay: HalfDayTypes.BEGINNING_OF_DAY,
      }),
      AttendanceShiftUtils.newShift<TimesheetPageShiftFragment>({
        date,
        employeeId,
        halfDay: HalfDayTypes.END_OF_DAY,
      }),
    ]
  }

  if (AttendanceShiftUtils.isFirstHalf(firstShift)) {
    return [
      firstShift,
      AttendanceShiftUtils.newShift<TimesheetPageShiftFragment>({
        date,
        employeeId,
        halfDay: HalfDayTypes.END_OF_DAY,
      }),
    ]
  } else {
    return [
      AttendanceShiftUtils.newShift<TimesheetPageShiftFragment>({
        date,
        employeeId,
        halfDay: HalfDayTypes.BEGINNING_OF_DAY,
      }),
      firstShift,
    ]
  }
}

const halfDayShifts = ({
  attendancePeriods,
  employeeId,
  shifts,
  rangeDays,
}: {
  attendancePeriods: AttendancePeriod[] | undefined
  employeeId: number
  shifts: TimesheetPageShiftFragment[]
  rangeDays: Moment[]
}) => {
  if (!attendancePeriods?.length || !rangeDays.length) return {}

  const shiftsHash: Record<string, TimesheetPageShiftFragment[]> = {}

  rangeDays.forEach((day: Moment) => {
    const date = dateToString(day, 'backend')
    shiftsHash[date] = halfDayShiftsFor({ date, employeeId, shifts })
  })

  return shiftsHash
}

const shiftCountForAYear = async (
  setHalfDayShiftCount: React.Dispatch<React.SetStateAction<number>>,
  year: number,
  employeeId: number
) => {
  const { value } = await asyncAction(
    () =>
      ApiClient().get('/attendance/shift_stats', {
        year: year,
        employee_id: employeeId,
      }),
    {
      successFlash: false,
    }
  )
  setHalfDayShiftCount(value.half_day_worked / 2)
}

interface Props {
  store: Store
}

const DailyShifts = ({ store }: Props) => {
  const { bulkDestroyShift, bulkCreateShift, deleteShift, createShift } = useShiftActions()
  const [halfDayShiftCount, setHalfDayShiftCount] = React.useState<number>(0)
  const { currentMonth } = store

  React.useEffect(() => {
    shiftCountForAYear(setHalfDayShiftCount, store.year, store.employeeId)
  }, [store.year, store.employeeId])

  const { featureByName } = useTimesheetData()

  const submitHalfDayShift = async (shift: TimesheetPageShiftFragment) => {
    if (!AttendanceShiftUtils.isNew(shift)) {
      const { success } = await asyncAction(
        () =>
          deleteShift({
            id: shift.id,
            date: shift.referenceDate,
            fetchDependencies: !(TimesheetFeatures.MATERIALIZED_DATA in featureByName),
          }),
        {
          successFlash: false,
          errorFlash: true,
        }
      )

      if (success) {
        setHalfDayShiftCount((prev) => prev - AttendanceShiftUtils.halfDayDuration(shift))
      }
    } else {
      const { success } = await asyncAction(
        () =>
          createShift({
            date: shift.date,
            referenceDate: shift.date,
            halfDay: shift.halfDay,
            employeeId: store.employeeId,
            workable: true,
            fetchDependencies: !(TimesheetFeatures.MATERIALIZED_DATA in featureByName),
          }),
        {
          successFlash: false,
          errorFlash: true,
        }
      )

      if (success) {
        setHalfDayShiftCount((prev) => prev + AttendanceShiftUtils.halfDayDuration(shift))
      }
    }
  }

  async function submitBulkCreate(
    shifts: TimesheetPageShiftFragment[],
    year: number,
    employeeId: number
  ) {
    app.setProgress({
      message: i18n.t('common.loading'),
    })

    const createShifts: BulkCreateAttendanceShiftShiftsInput[] = shifts.map((shift) => ({
      clockIn: shift.clockIn,
      clockOut: shift.clockOut,
      date: shift.date,
      day: stringToDate(shift.date, 'backend').date(),
      employeeId: shift.employeeId,
      halfDay: shift.halfDay,
      locationType: AttendanceShiftUtils.locationType(shift),
      observations: shift.observations,
      referenceDate: shift.referenceDate,
      source: 'desktop',
      timeSettingsBreakConfigurationId: shift.timeSettingsBreakConfiguration?.id,
      workable: shift.workable,
    }))

    const { success } = await asyncAction(
      () =>
        bulkCreateShift({
          shifts: createShifts,
        }),
      {
        successMessage: i18n.t('clock_in.bulk_action.create_success_message', {
          count: shifts.length / 2,
        }),
        errorFlash: true,
      }
    )
    app.setProgress(null)

    if (success) {
      shiftCountForAYear(setHalfDayShiftCount, year, employeeId)
    }
  }

  async function submitBulkDestroy(
    shifts: TimesheetPageShiftFragment[],
    year: number,
    employeeId: number
  ) {
    const ids = shifts.map((shift) => shift.id)

    const dateRange = getDateRange(currentMonth)
    const { startOn, endOfMonth: endOn } = dateRange

    const payload = {
      ids,
      employeeId,
      startOn,
      endOn,
    }

    app.setProgress({
      message: i18n.t('common.loading'),
    })

    const { success } = await asyncAction(() => bulkDestroyShift(payload), {
      successMessage: i18n.t('clock_in.bulk_action.destroy_success_message', {
        count: shifts.length / 2,
      }),
      errorFlash: true,
    })

    app.setProgress(null)

    if (success) {
      shiftCountForAYear(setHalfDayShiftCount, year, employeeId)
    }
  }

  const submitBulkChange = (shifts: TimesheetPageShiftFragment[]) => {
    if (!shifts.length) return

    const sortedShifts = shifts.sort(
      (a, b) => new Date(a.date).valueOf() - new Date(b.date).valueOf()
    )

    const isHalfDayShift = (shift: TimesheetPageShiftFragment | undefined) =>
      shift ? !AttendanceShiftUtils.isNew(shift) : false

    const isDefaultShift = (shift: TimesheetPageShiftFragment | undefined) =>
      shift ? AttendanceShiftUtils.isNew(shift) : false

    if (isHalfDayShift(sortedShifts[0])) {
      submitBulkDestroy(sortedShifts.filter(isHalfDayShift), store.year, store.employeeId)
    } else {
      submitBulkCreate(sortedShifts.filter(isDefaultShift), store.year, store.employeeId)
    }
  }

  const { shifts, reviewsStats, attendancePeriods } = useTimesheetData()
  const { dateFrom, dateTo } = useAttendanceDateNavigator()
  const rangeDays = AttendanceShiftUtils.daysBetweenDates(moment(dateFrom), moment(dateTo))

  return (
    <>
      <Box paddingBottom="s24">
        <DailyCounter year={currentMonth.year()} shiftCount={halfDayShiftCount} />
      </Box>
      <DailyTable
        store={store}
        submitHalfDayShift={submitHalfDayShift}
        submitBulkChange={submitBulkChange}
        shiftsHash={halfDayShifts({
          attendancePeriods,
          employeeId: store.employeeId,
          shifts,
          rangeDays,
        })}
        rangeDays={rangeDays}
        reviewsStats={reviewsStats}
      />
    </>
  )
}

export default DailyShifts
